/**
 * La Coochie header (sections/lc-header.liquid).
 *
 * - Hover opens the nav and cart drawers as NON-modal dialogs (no focus move, own scrim); a click makes the drawer
 *   modal (backdrop, scroll lock, focus). Touch devices always tap-to-modal. DECISIONS 2026-09-06 (7).
 *   The open panel covers its trigger, so "click the trigger while it is hover-open" is recognised as a press on
 *   the panel at the spot the pointer has rested on since the hover-open: the drawer upgrades to modal and the
 *   link that slid under the pointer is not followed.
 * - Light/dark swap over the home hero via IntersectionObserver (the desktop scroll container is .page-wrapper,
 *   so window scroll events are useless).
 * - Publishes --header-height on <html> and <body> from a ResizeObserver (Horizon's hero/product details read it
 *   from body; html needs it for scroll-padding-top — below 990px html is the scroll container). The value is
 *   bar + strip even while the strip is hidden, so the page padding never moves.
 * - Mobile announcement strip (< 768): hidden once the page has scrolled past it, back within a few px of the top
 *   (`.lc-header--strip-hidden`, animated in the section's CSS; Caroline 2026-09-23). Below 990px html scrolls, so
 *   window scroll events fire there; the listener is passive and rAF-throttled.
 * - `#cart` in the URL opens the cart drawer (emails and apps link to /cart#cart); `#lc-nav-signup` (the nav
 *   newsletter form's post-back anchor) opens the nav drawer so its success/error message is seen.
 * - Theme editor only (Shopify.designMode): every setting change re-renders the section and would close the nav
 *   drawer, so its open state is kept in sessionStorage and restored on re-render; selecting the section opens it,
 *   deselecting closes it. The `no-persist` attribute still keeps it out of Horizon's storefront restore.
 *
 * Built on Horizon primitives only: @theme/component, @theme/theme-drawer, @theme/utilities.
 */
import { Component } from '@theme/component';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { isClickedOutside, isTouchDevice, lockScroll, onAnimationEnd } from '@theme/utilities';

const HOVER_DELAY = 150;
/** sessionStorage key for the editor-only "nav drawer was open" flag. */
const EDITOR_NAV_KEY = 'lc-nav-editor-open';
const NAV_ID = 'lc-nav';
const inDesignMode = () => Boolean(window.Shopify?.designMode);
const LEAVE_DELAY = 300;
/** Added to the slide duration: hover is ignored this long after a close (a pointer that rested on the trigger
 *  while the panel slid off it is not a new hover). */
const CLOSE_GRACE = 200;
/** Pointer drift (px) that still counts as "resting on the trigger" after a hover-open. */
const STILL_TOLERANCE = 8;
/** A trigger-spot press swallows the click that follows within this window. */
const SWALLOW_WINDOW = 400;
/** The hidden mobile strip comes back once the page is within this many px of the top (iOS rubber-banding goes negative). */
const STRIP_SHOW_AT = 4;
/** The strip only exists below 768 (the section's CSS hides it from there). */
const stripMedia = matchMedia('(max-width: 767px)');

/** @param {{ clientX: number, clientY: number }} event @param {{ x: number, y: number }} point */
const distance = (event, point) => Math.hypot(event.clientX - point.x, event.clientY - point.y);

/**
 * Hover/click controller for one <theme-drawer> and its trigger in the header.
 * mode 'hover' = non-modal dialog.show(); mode 'modal' = dialog.showModal() opened by us;
 * no mode = opened by Horizon itself (its own open(), auto-open after add to cart, etc.).
 */
class DrawerHover {
  /** @type {Array<[EventTarget, string, EventListener, boolean]>} */
  #listeners = [];
  /** Element focused before a hover-open; focus goes back to it when the hover-mode drawer closes. */
  /** @type {Element | null} */
  #prevFocus = null;
  /** Last pointer position over the trigger; null once the pointer moves inside the open panel. */
  /** @type {{ x: number, y: number } | null} */
  #point = null;
  #closedAt = 0;
  #swallowUntil = 0;
  /** True once a real pointermove over the trigger has started (or skipped) the hover-intent timer. */
  #armed = false;

  /**
   * @param {HTMLElement & { close(): Promise<void> }} drawer
   * @param {HTMLElement | null} trigger
   * @param {boolean} hover
   */
  constructor(drawer, trigger, hover) {
    this.drawer = drawer;
    this.hoverEnabled = hover;
    this.inside = false;
    this.#listen(drawer, 'pointerenter', () => {
      this.inside = true;
      clearTimeout(this.leaveTimer);
    });
    this.#listen(drawer, 'pointerleave', () => {
      this.inside = false;
      this.leave();
    });
    this.#listen(drawer, 'pointermove', this.#onPanelMove);
    this.#listen(drawer, 'pointerdown', this.#onPanelDown);
    this.#listen(drawer, 'click', this.#onPanelClick, true);
    this.#listen(drawer, 'theme-drawer:open', this.#onExternalOpen);
    this.#listen(drawer, 'theme-drawer:close', this.#onClose);
    if (trigger) {
      this.#listen(trigger, 'pointerenter', this.enter);
      this.#listen(trigger, 'pointermove', this.track);
      this.#listen(trigger, 'pointerleave', this.leave);
    }
  }

  destroy() {
    clearTimeout(this.enterTimer);
    clearTimeout(this.leaveTimer);
    for (const [target, type, handler, capture] of this.#listeners) target.removeEventListener(type, handler, capture);
    this.#listeners = [];
    this.#reset();
  }

  get panel() {
    return /** @type {HTMLDialogElement | null} */ (this.drawer.querySelector('dialog'));
  }

  get isOpen() {
    return this.drawer.hasAttribute('open');
  }

  get mode() {
    return this.drawer.dataset.mode;
  }

  /**
   * Pointer over the trigger. Remembers where it is (a later press there must act on the trigger, not the panel) and
   * starts the hover-intent timer on the first real move: browsers also fire a synthetic pointerenter when the panel
   * slides off a resting pointer (no pointermove with it), which must not reopen the drawer.
   */
  track = (event) => {
    if (typeof event?.clientX !== 'number') return;
    this.#point = { x: event.clientX, y: event.clientY };
    if (event.type === 'pointermove' && !this.#armed) this.#arm();
  };

  enter = (event) => {
    if (!this.hoverEnabled) return;
    clearTimeout(this.leaveTimer);
    this.#armed = false;
    this.track(event);
  };

  leave = () => {
    clearTimeout(this.enterTimer);
    this.#armed = false;
    if (this.mode !== 'hover') return;
    clearTimeout(this.leaveTimer);
    this.leaveTimer = setTimeout(() => {
      if (!this.inside && this.mode === 'hover') this.#close();
    }, LEAVE_DELAY);
  };

  /** Click / tap on the trigger: open as a modal, or upgrade a hover-opened drawer to a modal. */
  click() {
    clearTimeout(this.enterTimer);
    clearTimeout(this.leaveTimer);
    if (!this.isOpen) {
      this.#show(true);
      return;
    }
    if (this.mode === 'hover') this.#upgrade();
  }

  /** Close a drawer this controller opened (scrim click). */
  close() {
    if (this.isOpen && this.mode) return this.#close();
  }

  #listen(target, type, handler, capture = false) {
    target.addEventListener(type, handler, capture);
    this.#listeners.push([target, type, handler, capture]);
  }

  #closeCooldown() {
    const seconds = parseFloat(getComputedStyle(this.drawer).getPropertyValue('--lc-drawer-duration'));
    return (Number.isFinite(seconds) ? seconds * 1000 : 500) + CLOSE_GRACE;
  }

  /** First real pointer move over the trigger: open after the intent delay unless a close just happened. */
  #arm() {
    this.#armed = true;
    if (!this.hoverEnabled || this.isOpen || performance.now() - this.#closedAt < this.#closeCooldown()) return;
    clearTimeout(this.enterTimer);
    this.enterTimer = setTimeout(() => this.#show(false), HOVER_DELAY);
  }

  #show(modal) {
    const panel = this.panel;
    if (!panel || panel.open) return;
    this.drawer.dataset.mode = modal ? 'modal' : 'hover';
    this.drawer.style.setProperty('--drawer-stack-order', '1'); // hover mode: above the header (menu-drawer + 1)
    panel.classList.add('theme-drawer__dialog--opening');
    if (modal) {
      this.#prevFocus = null;
      lockScroll(panel);
      panel.showModal();
    } else {
      // show() moves focus to the first link; a hover preview must not steal focus (or draw a focus ring there).
      const previous = document.activeElement;
      panel.show();
      this.#prevFocus = previous;
      this.#restoreFocus(previous);
    }
    onAnimationEnd(panel, () => panel.classList.remove('theme-drawer__dialog--opening'), { subtree: false });
    this.drawer.setAttribute('open', '');
    this.drawer.dispatchEvent(new DrawerOpenEvent());
    document.addEventListener('keydown', this.#onKey);
    panel.addEventListener('click', this.#onBackdrop);
    panel.addEventListener('cancel', this.#onCancel);
  }

  #upgrade() {
    const panel = this.panel;
    if (!panel || !panel.open || this.mode !== 'hover') return;
    this.#point = null;
    this.#prevFocus = null;
    panel.close();
    lockScroll(panel);
    panel.showModal();
    this.drawer.dataset.mode = 'modal';
  }

  /** Close through Horizon, then put focus back where it was if this was only a hover preview. */
  async #close() {
    const restore = this.mode === 'hover' ? this.#prevFocus ?? document.body : null;
    await this.drawer.close();
    if (restore) this.#restoreFocus(restore);
  }

  /** @param {Element | null} target */
  #restoreFocus(target) {
    if (target instanceof HTMLElement && target !== document.body && document.contains(target)) {
      target.focus({ preventScroll: true });
    } else if (document.activeElement instanceof HTMLElement && document.activeElement !== document.body) {
      document.activeElement.blur();
    }
  }

  #onPanelMove = (event) => {
    if (this.#point && distance(event, this.#point) > STILL_TOLERANCE) this.#point = null;
  };

  /** A press where the pointer has rested since the hover-open is a press on the covered trigger. */
  #onPanelDown = (event) => {
    if (this.mode !== 'hover' || !this.#point || event.button !== 0) return;
    if (distance(event, this.#point) > STILL_TOLERANCE) {
      this.#point = null;
      return;
    }
    this.#swallowUntil = performance.now() + SWALLOW_WINDOW;
    this.#upgrade();
  };

  #onPanelClick = (event) => {
    if (performance.now() > this.#swallowUntil) return;
    this.#swallowUntil = 0;
    event.preventDefault();
    event.stopPropagation();
  };

  #onClose = () => {
    this.#closedAt = performance.now();
    this.#reset();
  };

  /**
   * Horizon opened the drawer itself (Shopify.actions.openCart after an add, cart-drawer-component). Above its modal
   * breakpoint Horizon uses dialog.show() — no backdrop — so a click on the page left the bag open (Caroline 2026-09-24:
   * "when I click away from the shopping bag it should close"). Watch the document and close on an outside click; our
   * own opens set data-mode first and are modal (the backdrop click closes them), so they are skipped here.
   */
  #onExternalOpen = () => {
    if (this.mode) return;
    this.drawer.dataset.mode = 'external';
    document.addEventListener('click', this.#onOutside, true);
  };

  #onOutside = (event) => {
    if (this.mode !== 'external' || !this.isOpen || !(event.target instanceof Node)) return;
    if (this.panel?.contains(event.target)) return;
    this.drawer.close();
  };

  #onKey = (event) => {
    if (event.key === 'Escape' && this.mode && this.isOpen) {
      event.preventDefault();
      this.#close();
    }
  };

  #onBackdrop = (event) => {
    if (this.mode === 'modal' && this.panel && isClickedOutside(event, this.panel)) this.#close();
  };

  #onCancel = (event) => {
    event.preventDefault();
    this.#close();
  };

  #reset() {
    delete this.drawer.dataset.mode;
    this.#point = null;
    this.#prevFocus = null;
    document.removeEventListener('click', this.#onOutside, true);
    document.removeEventListener('keydown', this.#onKey);
    this.panel?.removeEventListener('click', this.#onBackdrop);
    this.panel?.removeEventListener('cancel', this.#onCancel);
  }
}

class LcHeader extends Component {
  requiredRefs = ['bar'];
  /** @type {DrawerHover | null} */ #nav = null;
  /** @type {DrawerHover | null} */ #cart = null;
  #resize = new ResizeObserver(() => this.#publishHeight());
  /** @type {IntersectionObserver | null} */ #io = null;
  #scrollFrame = 0;
  #stripHidden = false;
  /** True while the last input was a tap/click rather than a key (Caroline 2026-09-23: no ring on the close button after a tap). */
  #pointerInput = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('pointerdown', this.#onPointerDown, true);
    document.addEventListener('keydown', this.#onKeyDown, true);
    document.addEventListener('focusin', this.#onFocusIn);
    const hover = this.dataset.hover === 'true' && !isTouchDevice();
    const { burger, cartLink, strip } = this.refs;
    const nav = document.getElementById('lc-nav');
    const cart = document.getElementById('cart-drawer');
    if (nav) this.#nav = new DrawerHover(/** @type {any} */ (nav), burger instanceof HTMLElement ? burger : null, hover);
    if (cart) {
      this.#cart = new DrawerHover(/** @type {any} */ (cart), cartLink instanceof HTMLElement ? cartLink : null, hover);
    }

    document.addEventListener('theme-drawer:open', this.#syncExpanded);
    document.addEventListener('theme-drawer:close', this.#syncExpanded);

    this.#resize.observe(this);
    this.#publishHeight();
    this.#watchHero();

    this.#onHash();
    window.addEventListener('hashchange', this.#onHash);

    if (strip) {
      window.addEventListener('scroll', this.#onScroll, { passive: true });
      stripMedia.addEventListener('change', this.#onScroll);
      this.#updateStrip();
    }

    if (inDesignMode()) {
      document.addEventListener('shopify:section:select', this.#onSectionSelect);
      document.addEventListener('shopify:section:deselect', this.#onSectionDeselect);
      window.addEventListener('beforeunload', this.#forgetNav);
      if (sessionStorage.getItem(EDITOR_NAV_KEY) === '1') this.#openNav();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('pointerdown', this.#onPointerDown, true);
    document.removeEventListener('keydown', this.#onKeyDown, true);
    document.removeEventListener('focusin', this.#onFocusIn);
    this.#resize.disconnect();
    this.#io?.disconnect();
    this.#io = null;
    document.removeEventListener('theme-drawer:open', this.#syncExpanded);
    document.removeEventListener('theme-drawer:close', this.#syncExpanded);
    window.removeEventListener('hashchange', this.#onHash);
    window.removeEventListener('scroll', this.#onScroll);
    stripMedia.removeEventListener('change', this.#onScroll);
    cancelAnimationFrame(this.#scrollFrame);
    this.#scrollFrame = 0;
    document.removeEventListener('shopify:section:select', this.#onSectionSelect);
    document.removeEventListener('shopify:section:deselect', this.#onSectionDeselect);
    window.removeEventListener('beforeunload', this.#forgetNav);
    this.#nav?.destroy();
    this.#cart?.destroy();
    this.#nav = null;
    this.#cart = null;
  }

  /* ---- declarative handlers (on:*) ---- */
  navClick(event) {
    event.preventDefault();
    this.#nav?.click();
  }

  cartClick(event) {
    if (!this.#cart) return; // no drawer on this page (cart page): follow the link
    event.preventDefault();
    this.#cart.click();
  }

  closeDrawers() {
    this.#nav?.close();
    this.#cart?.close();
  }

  /* ---- internals ---- */
  #onHash = () => {
    if (location.hash === '#cart') this.#openCartDrawer();
    else if (location.hash === '#lc-nav-signup') this.#nav?.click();
  };

  #openCartDrawer() {
    const drawer = /** @type {any} */ (document.getElementById('cart-drawer'));
    if (!drawer) return;
    // The module defining <theme-drawer> may still be loading (fetchpriority=low).
    customElements.whenDefined('theme-drawer').then(() => drawer.open?.());
  }

  /** @param {Event} event */
  #onPointerDown = () => {
    this.#pointerInput = true;
  };

  #onKeyDown = () => {
    this.#pointerInput = false;
  };

  /**
   * Both drawers focus their close button when they open (showModal → first focusable; Horizon's theme-drawer does it
   * explicitly). Safari draws :focus-visible for script focus after a tap, so a tap-opened drawer showed a ring around
   * the ×. After a pointer input, focus parks on the dialog itself instead (still inside the modal, announced by its
   * label; the × stays first in the Tab order). Keyboard opens keep the ring.
   * @param {FocusEvent} event
   */
  #onFocusIn = (event) => {
    const target = event.target;
    if (!this.#pointerInput || !(target instanceof HTMLElement)) return;
    // closing a drawer hands focus back to its trigger (Horizon + DrawerHover); after a tap that drew the same ring on the burger
    if (target.matches('[data-testid="lc-burger"], [data-testid="lc-cart-icon"]')) {
      target.blur();
      return;
    }
    if (!target.matches('.lc-nav__close, #cart-drawer .theme-drawer__close-button')) return;
    const dialog = target.closest('dialog');
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;
    dialog.tabIndex = -1;
    dialog.focus({ preventScroll: true });
  };

  #syncExpanded = (event) => {
    const { burger, cartLink } = this.refs;
    const nav = document.getElementById(NAV_ID);
    const cart = document.getElementById('cart-drawer');
    const navOpen = Boolean(nav?.hasAttribute('open'));
    if (burger instanceof HTMLElement) burger.setAttribute('aria-expanded', String(navOpen));
    if (cartLink instanceof HTMLElement && cart) cartLink.setAttribute('aria-expanded', String(cart.hasAttribute('open')));
    if (inDesignMode() && event.target === nav) {
      if (navOpen) sessionStorage.setItem(EDITOR_NAV_KEY, '1');
      else this.#forgetNav();
    }
  };

  /* ---- theme editor: keep the nav drawer open across section re-renders ---- */
  #forgetNav = () => sessionStorage.removeItem(EDITOR_NAV_KEY);

  #openNav() {
    const nav = /** @type {any} */ (document.getElementById(NAV_ID));
    if (!nav) return;
    // theme-drawer.js loads with fetchpriority=low; open() needs the upgraded element.
    customElements.whenDefined('theme-drawer').then(() => {
      if (nav.isConnected && !nav.hasAttribute('open')) nav.open?.();
    });
  }

  /** The header section was selected in the editor sidebar: show the drawer so its copy settings are visible. */
  #onSectionSelect = (event) => {
    if (event.target instanceof Element && event.target.contains(this)) this.#openNav();
  };

  #onSectionDeselect = (event) => {
    if (!(event.target instanceof Element && event.target.contains(this))) return;
    this.#forgetNav();
    const nav = /** @type {any} */ (document.getElementById(NAV_ID));
    if (nav?.hasAttribute('open')) nav.close?.();
  };

  /**
   * --header-height = bar + strip, measured on the two boxes rather than on the component: the hidden strip is pulled
   * up by a negative margin (its own height is unchanged), so the value holds while it slides and the page padding
   * (.content-for-layout) never moves. From 768 the strip is display: none and measures 0.
   */
  #publishHeight() {
    const { bar, strip } = this.refs;
    const stripHeight = strip instanceof HTMLElement ? strip.offsetHeight : 0;
    const height = Math.round(bar.getBoundingClientRect().height + stripHeight);
    if (height <= 0) return;
    if (stripHeight > 0) this.style.setProperty('--lc-strip-height', `${stripHeight}px`);
    for (const element of [document.documentElement, document.body]) {
      element.style.setProperty('--header-height', `${height}px`);
    }
  }

  /* ---- mobile strip: hide on scroll, show at the top ---- */
  #onScroll = () => {
    if (this.#scrollFrame) return;
    this.#scrollFrame = requestAnimationFrame(() => {
      this.#scrollFrame = 0;
      this.#updateStrip();
    });
  };

  /** Hysteresis: hide once scrolled past the strip's height, show again within STRIP_SHOW_AT px of the top. */
  #updateStrip() {
    const { strip } = this.refs;
    if (!(strip instanceof HTMLElement)) return;
    const y = window.scrollY;
    let hidden = this.#stripHidden;
    if (!stripMedia.matches || y < STRIP_SHOW_AT) hidden = false;
    else if (y > strip.offsetHeight) hidden = true;
    if (hidden === this.#stripHidden) return;
    this.#stripHidden = hidden;
    this.classList.toggle('lc-header--strip-hidden', hidden);
  }

  /** Light (white) header while the home hero is under it; dark once it scrolls past. */
  #watchHero() {
    if (this.dataset.lightStart !== 'true') return;
    const first = document.querySelector('#MainContent > .shopify-section');
    if (!first) return;
    this.#io = new IntersectionObserver(
      ([entry]) => {
        const passed = entry.boundingClientRect.bottom <= 60;
        this.dataset.light = String(!passed);
      },
      { rootMargin: '-60px 0px 0px 0px', threshold: [0, 0.01] }
    );
    this.#io.observe(first);
  }
}

if (!customElements.get('lc-header-component')) {
  customElements.define('lc-header-component', LcHeader);
}
